/**
 * useCypherExplorationGraph — builds a ReactFlow node/edge graph from the
 * cypher-based exploration atoms (InitRadialLayoutAtom / ExpandNodeAtom).
 *
 * Drop-in replacement for useCollaboratorsGraph. Returns the same
 * `CollaboratorsGraphResult` shape so that CollaboratorsGraphView renders
 * without modification.
 *
 * Data flow:
 *   LayoutNodesAtom (Map<ARI, LayoutNode>)  — positional graph skeleton
 *   HydratedNodesAtom (Map<ARI, DisplayNode>) — display-layer data, already
 *     enriched with typenameDisplay / productKey / productIcon / productLogoUrl
 *   CrossEdgesAtom — edges between nodes that share a discovered neighbour
 *   LayoutStatusAtom — 'idle' | 'probing-mode-a' | 'ready' | 'expanding' | 'error'
 *
 * This hook is a pure projection: it assembles the ReactFlow payload from
 * already-computed atoms. All display resolution (icon, product label,
 * typename rebrand) lives in `toDisplayNode`; nothing derives fields here.
 */

import { useMemo, useRef } from 'react';

import type { Node, Edge } from '@xyflow/react';
import { useAtomValue } from 'jotai';

import {
	LayoutNodesAtom,
	HydratedNodesAtom,
	CrossEdgesAtom,
	LayoutStatusAtom,
} from './atoms/layoutAtoms';
import { AppearingNodesAtom } from './atoms/recencyAtoms';
import type { CollaboratorsGraphResult } from './graphTypes';
import type { FloatingEdgeData } from './nodes/FloatingEdge';
import type { ObjectNodeData } from './nodes/ObjectNode';
import type { DisplayNode } from './services/displayNode';
import { placeholderDisplayNode } from './services/displayNode';
import { isUserTypename } from './services/userTypes';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Projects a `DisplayNode` (or synthesises a placeholder when hydration
 * hasn't landed yet) into the `ObjectNodeData` payload ReactFlow renders.
 *
 * The display layer is already fully resolved upstream, so this is a
 * straight field copy; no ARI parsing or product fallbacks happen here.
 */
function buildNodeData(
	ari: string,
	typename: string,
	hydrated: Map<string, DisplayNode>,
): ObjectNodeData {
	const display = hydrated.get(ari) ?? placeholderDisplayNode(ari, typename);
	return {
		id: display.id,
		typename: display.typename,
		typenameDisplay: display.typenameDisplay,
		displayName: display.displayName,
		url: display.url,
		properties: display.properties,
		productIcon: display.productIcon ?? undefined,
		productIconImportPath: display.productIconImportPath ?? undefined,
		productLogoUrl: display.productLogoUrl,
		pictureUrl: display.pictureUrl ?? undefined,
	};
}

function isUserNodeType(nodeData: ObjectNodeData, fallbackTypename: string): boolean {
	return isUserTypename(nodeData.typename, fallbackTypename);
}

/**
 * Shallow equality check used to decide whether the previously cached
 * ObjectNodeData payload is structurally identical to a freshly built
 * one. Avoids producing a new node object when nothing observable has
 * actually changed — preserves React/ReactFlow identity across renders
 * that are triggered purely by transient atoms (e.g. AppearingNodesAtom
 * ticking during the spring animation).
 */
function shallowEqualNodeData(a: ObjectNodeData, b: ObjectNodeData): boolean {
	if (a === b) return true;
	const ak = Object.keys(a) as Array<keyof ObjectNodeData>;
	const bk = Object.keys(b) as Array<keyof ObjectNodeData>;
	if (ak.length !== bk.length) return false;
	for (const k of ak) {
		if (a[k] !== b[k]) return false;
	}
	return true;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Converts the cypher exploration atoms into ReactFlow nodes and edges
 * that CollaboratorsGraphView can render directly.
 */
export function useCypherExplorationGraph(
	userNode: ObjectNodeData | null,
): CollaboratorsGraphResult {
	const layoutNodes = useAtomValue(LayoutNodesAtom);
	const hydratedNodes = useAtomValue(HydratedNodesAtom);
	const crossEdges = useAtomValue(CrossEdgesAtom);
	const status = useAtomValue(LayoutStatusAtom);
	const appearingNodes = useAtomValue(AppearingNodesAtom);

	const isLoading = status === 'idle' || status === 'probing-mode-a';
	const hasLoaded = status === 'ready' || status === 'expanding';

	// Identity-preserving caches. We reuse the *exact same* node/edge
	// object reference across renders whenever every observable field
	// (id, type, position, data, source/target) matches the previously
	// built one for the same id. This stops surviving nodes from
	// looking like brand-new entries when transient atoms such as
	// `AppearingNodesAtom` mutate during the spring animation.
	//
	// Without this, every appearing-tick produced a fresh node object
	// for *every* node, which then propagated through the
	// `CollaboratorsGraphView` sync effect and caused ReactFlow to
	// remount nodes mid-spring (cursor flicker between point/grab,
	// hover label flickering on/off, edge appear/disappear).
	const nodeCacheRef = useRef<Map<string, Node<ObjectNodeData>>>(new Map());
	const edgeCacheRef = useRef<Map<string, Edge<FloatingEdgeData>>>(new Map());
	// Cache of the LAST emitted outer arrays. If a memo run produces an
	// element-wise-identical array (same length, every index a `===`
	// reference match), we return the previously-emitted array instead
	// of the freshly-built one. This keeps `collaboratorsGraph.nodes`
	// reference-stable across appearingNodes ticks (which mutate the
	// recency atom every ~16 ms during the spring), so the merge
	// effect in `CollaboratorsGraphView` short-circuits its dep
	// comparison and avoids a redundant `setGraphNodes(merged)` →
	// prop-sync round-trip on every spring frame.
	const lastNodesArrayRef = useRef<Node<ObjectNodeData>[] | null>(null);
	const lastEdgesArrayRef = useRef<Edge<FloatingEdgeData>[] | null>(null);

	const result = useMemo(() => {
		const emptyResult: Omit<CollaboratorsGraphResult, 'isLoading' | 'hasLoaded'> = {
			nodes: [],
			edges: [],
			clusterMap: {},
			subClusterMap: {},
			clusters: [],
		};

		if (!userNode || layoutNodes.size === 0) {
			// Drop caches so a fresh exploration starts clean.
			nodeCacheRef.current = new Map();
			edgeCacheRef.current = new Map();
			lastNodesArrayRef.current = null;
			lastEdgesArrayRef.current = null;
			return emptyResult;
		}

		const prevNodes = nodeCacheRef.current;
		const prevEdges = edgeCacheRef.current;
		const nextNodes = new Map<string, Node<ObjectNodeData>>();
		const nextEdges = new Map<string, Edge<FloatingEdgeData>>();

		const nodes: Node<ObjectNodeData>[] = [];
		const edges: Edge<FloatingEdgeData>[] = [];

		for (const [ari, layoutNode] of layoutNodes) {
			const nodeData = buildNodeData(ari, layoutNode.sizingInput.typename, hydratedNodes);
			const isUser =
				!layoutNode.parentId || isUserNodeType(nodeData, layoutNode.sizingInput.typename);
			const nextType = isUser ? 'userObjectNode' : 'objectNode';

			// The `isAppearing` flag drives the fade-in transition on
			// first mount. The node component only reads it once (via
			// `useLayoutEffect` at mount) so subsequent re-renders don't
			// fight the transition.
			//
			// IMPORTANT: always set the `isAppearing` key (true or
			// false) so the data object's *shape* doesn't change when
			// the appearing TTL elapses. If the key were conditionally
			// included, `shallowEqualNodeData` would see different
			// keysets on the appear→settle transition and treat it as
			// a data change → produce a new object identity for the
			// node → ReactFlow remount mid-spring (cursor flicker, etc.).
			const dataWithAppear: ObjectNodeData = {
				...nodeData,
				isAppearing: appearingNodes.has(ari),
			};

			const prev = prevNodes.get(ari);
			let nextNode: Node<ObjectNodeData>;
			if (
				prev &&
				prev.type === nextType &&
				prev.position.x === layoutNode.x &&
				prev.position.y === layoutNode.y &&
				shallowEqualNodeData(prev.data as ObjectNodeData, dataWithAppear)
			) {
				// Identical to last render — reuse the *same* object
				// reference so React/ReactFlow sees no change.
				nextNode = prev;
			} else if (prev) {
				// Surviving node with a real change. We mutate `data` /
				// `type` / `position` in place on the cached `prev`
				// object rather than spreading into a new one, so the
				// outer-array stability check below (`nodes.every((n,
				// i) => n === prevNodesArr[i])`) keeps hitting and the
				// memo returns the same outer array reference. That in
				// turn lets the merge effect in `CollaboratorsGraphView`
				// short-circuit on its dep comparison and avoid a
				// redundant `setGraphNodes` write per spring frame.
				// Mirrors the in-place pattern already used for
				// `zIndex` in `CollaboratorsGraphView`.
				if (prev.type !== nextType) {
					// eslint-disable-next-line no-param-reassign
					(prev as { type: string }).type = nextType;
				}
				if (prev.position.x !== layoutNode.x || prev.position.y !== layoutNode.y) {
					// eslint-disable-next-line no-param-reassign
					prev.position = { x: layoutNode.x, y: layoutNode.y };
				}
				if (!shallowEqualNodeData(prev.data as ObjectNodeData, dataWithAppear)) {
					// eslint-disable-next-line no-param-reassign
					prev.data = dataWithAppear;
				}
				nextNode = prev;
			} else {
				// Genuine new arrival.
				//
				// We emit the node at its REST position (no origin
				// offset). The magnetic hook's `useLayoutEffect` puts
				// the node into `pendingMeasureRef` and defers the
				// origin seed until ReactFlow's ResizeObserver delivers
				// `measured`; from there the spring carries it from
				// origin → rest. The `isAppearing` flag drives the
				// opacity fade-in over the same frames.
				nextNode = {
					id: ari,
					type: nextType,
					position: { x: layoutNode.x, y: layoutNode.y },
					data: dataWithAppear,
					draggable: true,
				};
			}

			nextNodes.set(ari, nextNode);
			nodes.push(nextNode);

			if (layoutNode.parentId) {
				const edgeId = `edge-${layoutNode.parentId}-${ari}`;
				if (!nextEdges.has(edgeId)) {
					const nextData: FloatingEdgeData = layoutNode.parentRelLabel
						? { label: layoutNode.parentRelLabel }
						: {};
					const prevEdge = prevEdges.get(edgeId);
					let edge: Edge<FloatingEdgeData>;
					if (
						prevEdge &&
						prevEdge.source === layoutNode.parentId &&
						prevEdge.target === ari &&
						(prevEdge.data?.label ?? undefined) === (nextData.label ?? undefined)
					) {
						edge = prevEdge;
					} else {
						edge = {
							id: edgeId,
							type: 'floatingEdge',
							source: layoutNode.parentId,
							target: ari,
							data: nextData,
						};
					}
					nextEdges.set(edgeId, edge);
					edges.push(edge);
				}
			}
		}

		for (const cross of crossEdges) {
			const edgeId = `cross-${cross.from}-${cross.to}`;
			if (!nextEdges.has(edgeId)) {
				const nextData: FloatingEdgeData = cross.label ? { label: cross.label } : {};
				const prevEdge = prevEdges.get(edgeId);
				let edge: Edge<FloatingEdgeData>;
				if (
					prevEdge &&
					prevEdge.source === cross.from &&
					prevEdge.target === cross.to &&
					(prevEdge.data?.label ?? undefined) === (nextData.label ?? undefined)
				) {
					edge = prevEdge;
				} else {
					edge = {
						id: edgeId,
						type: 'floatingEdge',
						source: cross.from,
						target: cross.to,
						data: nextData,
					};
				}
				nextEdges.set(edgeId, edge);
				edges.push(edge);
			}
		}

		// Commit new caches; orphaned ids drop out automatically.
		nodeCacheRef.current = nextNodes;
		edgeCacheRef.current = nextEdges;

		// Outer-array stability: if the just-built `nodes` array is
		// element-wise-identical to the previously emitted one (same
		// length, every index a `===` reference match), reuse the prior
		// array reference. Likewise for `edges`. See `lastNodesArrayRef`
		// docstring for why this matters (xyflow's prop-sync clobber).
		const prevNodesArr = lastNodesArrayRef.current;
		const stableNodes =
			prevNodesArr !== null &&
			prevNodesArr.length === nodes.length &&
			nodes.every((n, i) => n === prevNodesArr[i])
				? prevNodesArr
				: nodes;
		lastNodesArrayRef.current = stableNodes;

		const prevEdgesArr = lastEdgesArrayRef.current;
		const stableEdges =
			prevEdgesArr !== null &&
			prevEdgesArr.length === edges.length &&
			edges.every((e, i) => e === prevEdgesArr[i])
				? prevEdgesArr
				: edges;
		lastEdgesArrayRef.current = stableEdges;

		return {
			nodes: stableNodes,
			edges: stableEdges,
			clusterMap: {},
			subClusterMap: {},
			clusters: [],
		};
	}, [userNode, layoutNodes, hydratedNodes, crossEdges, appearingNodes]);

	return {
		...result,
		isLoading,
		hasLoaded,
	};
}
